import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import allImages from "../../assets/images-import.jsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../../Interceptors/axiosInstance.jsx";
import { getPasswordResetPath } from "../../utils/urlTokenAuth";
import {
  resendLoginOtp,
  sendLoginOtp,
  verifyLoginOtp,
} from "../../services/authService";

export const SignInContent = () => {
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [otpUserId, setOtpUserId] = useState("");
  const [otp, setOtp] = useState("");
  const [isOtpMode, setIsOtpMode] = useState(false);
  const [otpResendCountdown, setOtpResendCountdown] = useState(0);
  const [isOtpSubmitting, setIsOtpSubmitting] = useState(false);
  const [keepLogged, setKeepLogged] = useState(false);
  const [error, setError] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const navigate = useNavigate();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const redirectAfterLogin = localStorage.getItem("redirectAfterLogin");

  useEffect(() => {
    if (otpResendCountdown <= 0) return undefined;

    const countdownTimer = setTimeout(() => {
      setOtpResendCountdown((seconds) => Math.max(0, seconds - 1));
    }, 1000);

    return () => clearTimeout(countdownTimer);
  }, [otpResendCountdown]);

  useEffect(() => {
    const urlToken = searchParams.get("token")?.trim();
    if (!urlToken) return;

    if (Cookies.get("accessToken")) {
      navigate("/dashboard", { replace: true });
      return;
    }

    navigate(getPasswordResetPath(urlToken), { replace: true });
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError("Please enter both User Name and password");
      return;
    }
    setError("");

    const loginData = {
      userName: username.trim(),
      password: password.trim(),
      keepLogged: keepLogged ? "true" : "false",
    };

    try {
      setIsButtonDisabled(true);
      const response = await axiosInstance.post("auth/login", loginData, {
        skipAuth: true,
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      });
      setIsButtonDisabled(false);

      const { result, isSuccess, message } = response.data ?? {};

      if (!isSuccess || !result?.accessToken) {
        const apiMessage =
          result?.errors?.[0]?.message || message || "Login failed";
        throw new Error(apiMessage);
      }

      Cookies.set("accessToken", result.accessToken, { expires: 1 });
      Cookies.set("refreshToken", result.refreshToken, { expires: 7 });

      if (redirectAfterLogin) {
        navigate(redirectAfterLogin);
        localStorage.removeItem("redirectAfterLogin");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      setIsButtonDisabled(false);
      console.error("Error during login:", error);

      const apiData = error.response?.data;
      const apiMessage =
        apiData?.result?.errors?.[0]?.message ||
        apiData?.message ||
        error.message;

      setError(
        apiMessage ||
          "Login failed. Please check your User Name and password and try again."
      );
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    const trimmedEmail = emailAddress.trim();

    if (!/\S+@\S+\.\S+/.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    setError("");
    setIsOtpSubmitting(true);
    try {
      const response = await sendLoginOtp(trimmedEmail);
      const userId = response?.result?.userId;
      if (!response?.isSuccess || !userId) {
        throw new Error(response?.message || "Unable to send verification code.");
      }

      setOtpUserId(userId);
      setOtp("");
      setOtpResendCountdown(60);
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          requestError.message ||
          "Unable to send verification code. Please try again."
      );
    } finally {
      setIsOtpSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (!otpUserId || otpResendCountdown > 0) return;

    setError("");
    setIsOtpSubmitting(true);
    try {
      const response = await resendLoginOtp(otpUserId);
      const returnedUserId = response?.result?.userId;
      if (!response?.isSuccess || !returnedUserId) {
        throw new Error(response?.message || "Unable to resend verification code.");
      }

      setOtpUserId(returnedUserId);
      setOtp("");
      setOtpResendCountdown(60);
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          requestError.message ||
          "Unable to resend verification code. Please try again."
      );
    } finally {
      setIsOtpSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpUserId || !/^\d{6}$/.test(otp)) {
      setError("Enter the 6-digit verification code sent to your email.");
      return;
    }

    setError("");
    setIsOtpSubmitting(true);
    try {
      const response = await verifyLoginOtp({ userId: otpUserId, otp });
      const { result } = response ?? {};
      if (!response?.isSuccess || !result?.accessToken) {
        throw new Error(response?.message || "Unable to verify the code.");
      }

      Cookies.set("accessToken", result.accessToken, { expires: 1 });
      if (result.refreshToken) {
        Cookies.set("refreshToken", result.refreshToken, { expires: 7 });
      }

      if (redirectAfterLogin) {
        navigate(redirectAfterLogin);
        localStorage.removeItem("redirectAfterLogin");
      } else {
        navigate("/dashboard");
      }
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          requestError.message ||
          "Unable to verify the code. Please try again."
      );
    } finally {
      setIsOtpSubmitting(false);
    }
  };

  const handleOtpModeChange = (enabled) => {
    setIsOtpMode(enabled);
    setError("");
    setOtpUserId("");
    setOtp("");
    setOtpResendCountdown(0);
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prevState) => !prevState);
  };

  return (
    <>
      <style>
        {`
          .form-check-input:checked {
            background-color: #282059 !important;
            border-color: #282059 !important;
          }
          .signin-hero {
            min-height: 280px;
          }
          @media (min-width: 992px) {
            .signin-hero {
              min-height: 100%;
            }
          }
          .auth-mode-link {
            background: transparent;
            border: 0;
            box-shadow: none !important;
            color: #4b38b3;
            font-weight: 600;
            text-decoration: none;
            padding: 0;
            transition: color 150ms ease, text-decoration-color 150ms ease;
          }
          .auth-mode-link:hover {
            color: #282059;
            text-decoration: underline;
            text-underline-offset: 3px;
          }
          .auth-mode-link:focus-visible {
            border-radius: 3px;
            outline: 2px solid #4b38b3;
            outline-offset: 3px;
          }
        `}
      </style>

      <div className="auth-page-wrapper auth-bg-cover py-5 d-flex justify-content-center align-items-center min-vh-100">
        <div className="auth-page-content overflow-hidden pt-lg-5">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div
                  className="card overflow-hidden"
                  style={{ boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px" }}
                >
                  <div className="row g-0 align-items-stretch">
                    <div
                      className="col-lg-6 auth-one-bg signin-hero"
                      style={{
                        backgroundImage: `url(${allImages.SigninPageBack})`,
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "cover",
                      }}
                    ></div>

                    <div className="col-lg-6">
                      <div className="card-body p-md-5 mx-md-4">
                        <div className="text-center mt-2 mb-2">
                          <a
                            href="https://www.jltcabz.com/Default.aspx"
                            className="d-inline-block auth-logo"
                            style={{
                              marginBottom: "30px",
                            }}
                          >
                            <img
                              src={allImages.logoDark}
                              alt="Logo"
                              height="60"
                            />
                          </a>
                        </div>
                        <div className="text-center">
                          <h4
                            className="fw-bolder"
                            style={{
                              color: "#000",
                              fontSize: "16px",
                              marginBottom: "4px",
                            }}
                          >
                            Welcome Back!
                          </h4>
                          <p>Log in to continue to BLS Admin</p>
                        </div>

                        <form onSubmit={isOtpMode ? (otpUserId ? handleVerifyOtp : handleSendOtp) : handleSubmit}>
                          {error && (
                            <div className="alert alert-danger">{error}</div>
                          )}
                          {isOtpMode ? (
                            <>
                              {!otpUserId ? (
                                <div className="mb-4 mt-2">
                                  <label htmlFor="otpEmail" className="form-label">
                                    Email address
                                  </label>
                                  <input
                                    type="email"
                                    className="form-control"
                                    id="otpEmail"
                                    style={{ minHeight: "50px" }}
                                    placeholder="Enter your registered email"
                                    value={emailAddress}
                                    onChange={(e) => setEmailAddress(e.target.value)}
                                    required
                                  />
                                </div>
                              ) : (
                                <>
                                  <p className="text-muted mt-3">
                                    Enter the 6-digit code sent to {emailAddress.trim()}.
                                  </p>
                                  <div className="mb-4">
                                    <label htmlFor="loginOtp" className="form-label">
                                      Verification code
                                    </label>
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      autoComplete="one-time-code"
                                      className="form-control text-center"
                                      id="loginOtp"
                                      style={{ minHeight: "50px", letterSpacing: "0.5em" }}
                                      placeholder="------"
                                      value={otp}
                                      onChange={(e) =>
                                        setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                                      }
                                      maxLength={6}
                                      required
                                    />
                                  </div>
                                  <div className="text-end mb-3">
                                    <button
                                      type="button"
                                      className="btn btn-link p-0"
                                      onClick={handleResendOtp}
                                      disabled={isOtpSubmitting || otpResendCountdown > 0}
                                    >
                                      {otpResendCountdown > 0
                                        ? `Resend code in ${otpResendCountdown}s`
                                        : "Resend code"}
                                    </button>
                                  </div>
                                </>
                              )}
                              <div className="text-center pt-1 mt-4 pb-1">
                                <button
                                  type="submit"
                                  className="btn btn-secondary btn-primary-login w-100"
                                  disabled={isOtpSubmitting}
                                >
                                  {isOtpSubmitting
                                    ? otpUserId
                                      ? "Verifying..."
                                      : "Sending..."
                                    : otpUserId
                                      ? "Verify and Sign In"
                                      : "Send Verification Code"}
                                </button>
                              </div>
                              <div className="text-center mt-3">
                                <button
                                  type="button"
                                  className="btn auth-mode-link"
                                  onClick={() => handleOtpModeChange(false)}
                                >
                                  <i className="ri-lock-password-line me-1" aria-hidden="true"></i>
                                  Sign in with password
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                          <div className="mb-4 mt-2">
                            <label htmlFor="txtUserName" className="form-label">
                              User Name
                            </label>
                            <div className="form-outline position-relative auth-pass-inputgroup">
                              <input
                                type="text"
                                className="form-control"
                                id="txtUserName"
                                style={{ minHeight: "50px" }}
                                placeholder="Enter User Name"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                              />
                            </div>
                          </div>

                          <div className="mb-4 mt-2">
                            <label htmlFor="txtPassword" className="form-label">
                              Password
                            </label>
                            <div className="form-outline position-relative auth-pass-inputgroup">
                              <input
                                type={isPasswordVisible ? "text" : "password"}
                                className="form-control pe-5"
                                id="txtPassword"
                                style={{ minHeight: "50px" }}
                                placeholder="Enter password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                              />
                              <button
                                type="button"
                                style={{ boxShadow: "none" }}
                                className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted shadow-none password-addon mt-2"
                                onClick={togglePasswordVisibility}
                              >
                                <i
                                  className={
                                    isPasswordVisible
                                      ? "mdi mdi-eye-off align-middle"
                                      : "mdi mdi-eye align-middle"
                                  }
                                ></i>
                              </button>
                            </div>
                          </div>

                          <div className="d-flex justify-content-between align-items-center mt-2">
                            <div className="form-check mb-0">
                              <input
                                className="form-check-input me-2"
                                type="checkbox"
                                checked={keepLogged}
                                onChange={(e) =>
                                  setKeepLogged(e.target.checked)
                                }
                                id="chkLogKeep"
                              />
                              <label
                                className="form-check-label"
                                htmlFor="chkLogKeep"
                              >
                                Remember me
                              </label>
                            </div>
                            <button
                              type="button"
                              className="btn auth-mode-link"
                              onClick={() => handleOtpModeChange(true)}
                            >
                              {/* <i className="ri-mail-line me-1" aria-hidden="true"></i> */}
                              Sign in with email code
                            </button>
                          </div>

                          <div className="text-center pt-1 mt-4 pb-1">
                            <button
                              type="submit"
                              className="btn btn-secondary btn-primary-login w-100"
                              disabled={isButtonDisabled}
                            >
                              {isButtonDisabled ? "Signing In..." : "Log In"}
                            </button>
                          </div>
                            </>
                          )}
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
